import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Bug, Search, Filter, AlertTriangle, Loader2, CheckCircle, XCircle, Clock, Eye, Users, BarChart3, ChevronRight, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  adminGetBugReports, adminGetBugSummary, adminChangeBugStatus, adminAssignBug,
  adminMarkBugDuplicate, adminAddBugNote, adminDeleteBugAttachment,
  adminBulkBugAction, adminGetBugAdmins, getBugReport, adminGetCriticalAlerts, adminDismissCritical,
  normalizeFileUrl,
} from '@/lib/api';

const SEV = {
  low: { cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800', dot: 'bg-gray-400' },
  medium: { cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30', dot: 'bg-amber-400' },
  high: { cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30', dot: 'bg-orange-500' },
  critical: { cls: 'bg-red-100 text-red-700 dark:bg-red-900/30', dot: 'bg-red-500' },
};
const STATUS_MAP = {
  NEW: { icon: Clock, cls: 'bg-blue-100 text-blue-700' },
  IN_REVIEW: { icon: Eye, cls: 'bg-indigo-100 text-indigo-700' },
  IN_PROGRESS: { icon: Loader2, cls: 'bg-yellow-100 text-yellow-700' },
  RESOLVED: { icon: CheckCircle, cls: 'bg-green-100 text-green-700' },
  CLOSED: { icon: XCircle, cls: 'bg-gray-100 text-gray-600' },
  DUPLICATE: { icon: Bug, cls: 'bg-purple-100 text-purple-700' },
  WONT_FIX: { icon: XCircle, cls: 'bg-gray-100 text-gray-600' },
};
const STATUSES = ['NEW', 'IN_REVIEW', 'IN_PROGRESS', 'RESOLVED', 'CLOSED', 'DUPLICATE', 'WONT_FIX'];
const CATEGORIES = ['listing_issue', 'payment_issue', 'account_issue', 'search_matching', 'invoice_billing', 'email_not_received', 'performance_loading', 'other'];
const CAT_LABELS = { listing_issue: 'Listing', payment_issue: 'Payment', account_issue: 'Account', search_matching: 'Search', invoice_billing: 'Invoice', email_not_received: 'Email', performance_loading: 'Performance', other: 'Other' };

export const AdminBugReportsTab = () => {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [criticalAlerts, setCriticalAlerts] = useState([]);

  // Filters
  const [filters, setFilters] = useState({ status: '', severity: '', category: '', search: '' });
  const [page, setPage] = useState(0);

  // Detail
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Action dialogs
  const [statusDialog, setStatusDialog] = useState(null);
  const [statusNote, setStatusNote] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [assignDialog, setAssignDialog] = useState(null);
  const [assignTo, setAssignTo] = useState('');
  const [dupDialog, setDupDialog] = useState(null);
  const [dupRef, setDupRef] = useState('');
  const [dupNote, setDupNote] = useState('');
  const [noteDialog, setNoteDialog] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState('internal');

  // Bulk
  const [selected, setSelected] = useState([]);
  const [bulkDialog, setBulkDialog] = useState(false);
  const [bulkAction, setBulkAction] = useState('change_status');
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkNote, setBulkNote] = useState('');
  const [bulkAssignee, setBulkAssignee] = useState('');

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * 50, limit: 50 };
      if (filters.status) params.status = filters.status;
      if (filters.severity) params.severity = filters.severity;
      if (filters.category) params.category = filters.category;
      if (filters.search) params.search = filters.search;
      const res = await adminGetBugReports(params);
      setReports(res.data.reports);
      setTotal(res.data.total);
    } catch { toast.error(t('admin.common.loadError')); }
    finally { setLoading(false); }
  }, [page, filters]);

  const loadSummary = async () => {
    try {
      const [sumRes, adminRes, alertRes] = await Promise.all([
        adminGetBugSummary(), adminGetBugAdmins(), adminGetCriticalAlerts(),
      ]);
      setSummary(sumRes.data);
      setAdmins(adminRes.data);
      setCriticalAlerts(alertRes.data);
    } catch {}
  };

  useEffect(() => { loadReports(); }, [loadReports]);
  useEffect(() => { loadSummary(); }, []);

  const openDetail = async (reportId) => {
    setDetailLoading(true);
    try {
      const res = await getBugReport(reportId);
      setDetail(res.data);
    } catch { toast.error(t('admin.common.loadError')); }
    finally { setDetailLoading(false); }
  };

  const handleStatusChange = async () => {
    if (!statusDialog || !newStatus || !statusNote.trim()) return;
    try {
      await adminChangeBugStatus(statusDialog, { status: newStatus, note: statusNote });
      toast.success(`Status changed to ${newStatus}`);
      setStatusDialog(null);
      setStatusNote('');
      setNewStatus('');
      loadReports();
      loadSummary();
      if (detail?.report_id === statusDialog) openDetail(statusDialog);
    } catch (err) { toast.error(err.response?.data?.detail || t('admin.common.saveError')); }
  };

  const handleAssign = async () => {
    if (!assignDialog || !assignTo) return;
    try {
      await adminAssignBug(assignDialog, { admin_user_id: assignTo });
      toast.success(t('admin.bug.assignedSuccess'));
      setAssignDialog(null);
      setAssignTo('');
      loadReports();
      loadSummary();
      if (detail?.report_id === assignDialog) openDetail(assignDialog);
    } catch (err) { toast.error(err.response?.data?.detail || t('admin.common.saveError')); }
  };

  const handleDuplicate = async () => {
    if (!dupDialog || !dupRef.trim()) return;
    try {
      await adminMarkBugDuplicate(dupDialog, { original_report_id: dupRef, note: dupNote });
      toast.success(t('admin.bug.markedDuplicate'));
      setDupDialog(null);
      setDupRef('');
      setDupNote('');
      loadReports();
      if (detail?.report_id === dupDialog) openDetail(dupDialog);
    } catch (err) { toast.error(err.response?.data?.detail || t('admin.common.saveError')); }
  };

  const handleAddNote = async () => {
    if (!noteDialog || !noteText.trim()) return;
    try {
      await adminAddBugNote(noteDialog, { note: noteText, note_type: noteType });
      toast.success(t('admin.bug.noteAdded'));
      setNoteDialog(null);
      setNoteText('');
      if (detail?.report_id === noteDialog) openDetail(noteDialog);
    } catch (err) { toast.error(err.response?.data?.detail || t('admin.common.saveError')); }
  };

  const handleDeleteAttachment = async (reportId, attachmentId) => {
    if (!confirm('Delete this attachment?')) return;
    try {
      await adminDeleteBugAttachment(reportId, attachmentId);
      toast.success(t('admin.bug.attachmentDeleted'));
      if (detail?.report_id === reportId) openDetail(reportId);
    } catch (err) { toast.error(err.response?.data?.detail || t('admin.common.saveError')); }
  };

  const handleBulkAction = async () => {
    if (!selected.length) return;
    try {
      await adminBulkBugAction({
        report_ids: selected,
        action: bulkAction,
        status: bulkAction === 'change_status' ? bulkStatus : undefined,
        note: bulkNote || undefined,
        admin_user_id: bulkAction === 'reassign' ? bulkAssignee : undefined,
      });
      toast.success(`Updated ${selected.length} reports`);
      setBulkDialog(false);
      setSelected([]);
      loadReports();
    } catch (err) { toast.error(err.response?.data?.detail || t('admin.common.saveError')); }
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAll = () => setSelected(s => s.length === reports.length ? [] : reports.map(r => r.report_id));

  return (
    <div className="space-y-6" data-testid="admin-bug-reports-tab">
      {/* Critical alerts banner */}
      {criticalAlerts.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4" data-testid="critical-alerts-banner">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <span className="font-semibold text-red-700 dark:text-red-300">{criticalAlerts.length} Critical Report(s) Unassigned</span>
          </div>
          {criticalAlerts.map(a => (
            <div key={a.notification_id} className="flex items-center justify-between py-1">
              <span className="text-sm">{a.report_id}: {a.title}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openDetail(a.report_id)}>View</Button>
                <Button size="sm" variant="ghost" onClick={async () => { await adminDismissCritical(a.report_id); loadSummary(); }}>{t('admin.bug.dismiss')}</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="bug-summary-cards">
          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-3xl font-bold text-orange-500">{summary.total_open}</p>
              <p className="text-xs text-muted-foreground">{t('admin.bug.openReports')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-3xl font-bold text-red-500">{summary.open_by_severity?.critical || 0}</p>
              <p className="text-xs text-muted-foreground">{t('admin.bug.criticalOpen')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-3xl font-bold">{summary.submitted_this_week}</p>
              <p className="text-xs text-muted-foreground">{t('admin.bug.thisWeek')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-3xl font-bold">{summary.avg_resolution_hours ?? '—'}</p>
              <p className="text-xs text-muted-foreground">{t('admin.bug.avgResolution')}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder={t("admin.bug.searchByTitle")}
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            data-testid="bug-search-input"
          />
        </div>
        <Select value={filters.status} onValueChange={v => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-40" data-testid="bug-filter-status"><SelectValue placeholder={t("admin.bug.statusPh")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('admin.bug.allStatuses')}</SelectItem>
            {STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.severity} onValueChange={v => setFilters(f => ({ ...f, severity: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-36" data-testid="bug-filter-severity"><SelectValue placeholder="Severity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {['critical', 'high', 'medium', 'low'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.category} onValueChange={v => setFilters(f => ({ ...f, category: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-36" data-testid="bug-filter-category"><SelectValue placeholder={t("admin.bug.categoryPh")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{CAT_LABELS[c]}</SelectItem>)}
          </SelectContent>
        </Select>
        {selected.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => setBulkDialog(true)} data-testid="bug-bulk-action-btn">
            Bulk Action ({selected.length})
          </Button>
        )}
      </div>

      {/* Reports table */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
      ) : reports.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">{t('admin.bug.noReports')}</CardContent></Card>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800">
              <tr>
                <th className="p-3 text-left w-8"><Checkbox checked={selected.length === reports.length && reports.length > 0} onCheckedChange={toggleAll} /></th>
                <th className="p-3 text-left">Report</th>
                <th className="p-3 text-left">{t('admin.bug.severity')}</th>
                <th className="p-3 text-left">{t('admin.bug.category')}</th>
                <th className="p-3 text-left">{t('admin.bug.userSite')}</th>
                <th className="p-3 text-left">{t('admin.bug.statusPh')}</th>
                <th className="p-3 text-left">{t('admin.bug.assignee')}</th>
                <th className="p-3 text-left">Date</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {reports.map(r => {
                const sev = SEV[r.severity] || SEV.low;
                const st = STATUS_MAP[r.status] || STATUS_MAP.NEW;
                const StIcon = st.icon;
                return (
                  <tr
                    key={r.report_id}
                    className="border-t hover:bg-slate-50/50 dark:hover:bg-slate-800/50 cursor-pointer"
                    onClick={(e) => { if (e.target.type !== 'checkbox' && e.target.tagName !== 'INPUT') openDetail(r.report_id); }}
                    data-testid={`bug-row-${r.report_id}`}
                  >
                    <td className="p-3"><Checkbox checked={selected.includes(r.report_id)} onCheckedChange={() => toggleSelect(r.report_id)} /></td>
                    <td className="p-3">
                      <p className="font-mono text-xs text-muted-foreground">{r.report_id}</p>
                      <p className="font-medium truncate max-w-[200px]">{r.title}</p>
                    </td>
                    <td className="p-3"><Badge className={sev.cls} variant="secondary">{r.severity}</Badge></td>
                    <td className="p-3 text-xs">{CAT_LABELS[r.category] || r.category}</td>
                    <td className="p-3 text-xs">
                      <p>{r.submitted_by?.full_name}</p>
                      <p className="text-muted-foreground">{r.user_site?.site_name || r.user_group?.group_name || '—'}</p>
                    </td>
                    <td className="p-3"><Badge className={st.cls} variant="secondary"><StIcon className="h-3 w-3 mr-1" />{r.status.replace('_', ' ')}</Badge></td>
                    <td className="p-3 text-xs">{r.admin_assignee_name || '—'}</td>
                    <td className="p-3 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString('de-DE')}</td>
                    <td className="p-3"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {total > 50 && (
            <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-800 border-t">
              <span className="text-xs text-muted-foreground">{total} reports total</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)}>{t('admin.bug.previous')}</Button>
                <Button size="sm" variant="outline" disabled={(page + 1) * 50 >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Detail Dialog ── */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="sm:max-w-[750px] max-h-[85vh] overflow-y-auto" data-testid="admin-bug-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-orange-500">{detail.report_id}</span>
                  <Badge className={(SEV[detail.severity] || SEV.low).cls} variant="secondary">{detail.severity}</Badge>
                  <Badge className={(STATUS_MAP[detail.status] || STATUS_MAP.NEW).cls} variant="secondary">{detail.status.replace('_', ' ')}</Badge>
                </DialogTitle>
              </DialogHeader>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-2 mb-4">
                <Button size="sm" variant="outline" onClick={() => { setStatusDialog(detail.report_id); setNewStatus(''); setStatusNote(''); }} data-testid="admin-change-status-btn">{t('admin.bug.changeStatus')}</Button>
                <Button size="sm" variant="outline" onClick={() => { setAssignDialog(detail.report_id); setAssignTo(''); }} data-testid="admin-assign-btn">Assign</Button>
                <Button size="sm" variant="outline" onClick={() => { setDupDialog(detail.report_id); setDupRef(''); setDupNote(''); }} data-testid="admin-mark-duplicate-btn">{t('admin.bug.markDuplicate')}</Button>
                <Button size="sm" variant="outline" onClick={() => { setNoteDialog(detail.report_id); setNoteText(''); setNoteType('internal'); }} data-testid="admin-add-note-btn">{t('admin.bug.addNote')}</Button>
              </div>

              <Tabs defaultValue="details">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="details">{t('admin.bug.details')}</TabsTrigger>
                  <TabsTrigger value="notes">{t('admin.bug.notesHistory')}</TabsTrigger>
                  <TabsTrigger value="audit">{t('admin.bug.auditLog')}</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-4 mt-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">{t('admin.bug.submittedBy')}</p>
                      <p className="font-medium">{detail.submitted_by?.full_name} ({detail.submitted_by?.role})</p>
                      <p className="text-xs text-muted-foreground">{detail.submitted_by?.email}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t('admin.bug.siteGroup')}</p>
                      <p>{detail.user_site?.site_name || '—'} / {detail.user_group?.group_name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t('admin.bug.category')}</p>
                      <p>{CAT_LABELS[detail.category] || detail.category}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t('admin.bug.assignee')}</p>
                      <p>{detail.admin_assignee_name || 'Unassigned'}</p>
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <p className="text-sm font-medium mb-1">Title</p>
                    <p>{detail.title}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">{t('admin.bug.description')}</p>
                    <p className="text-sm whitespace-pre-wrap bg-slate-50 dark:bg-slate-800 p-3 rounded">{detail.description}</p>
                  </div>
                  {detail.steps_to_reproduce && (
                    <div>
                      <p className="text-sm font-medium mb-1">{t('admin.bug.stepsToReproduce')}</p>
                      <p className="text-sm whitespace-pre-wrap bg-slate-50 dark:bg-slate-800 p-3 rounded">{detail.steps_to_reproduce}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">{t('admin.bug.pageUrl')}</p>
                      <p className="break-all text-xs">{detail.page_url || '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t('admin.bug.browserDevice')}</p>
                      <p className="text-xs">{detail.browser_info || '—'}</p>
                    </div>
                  </div>
                  {detail.attachments?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium mb-1">{t('admin.bug.attachments')}</p>
                      <div className="flex gap-2 flex-wrap">
                        {detail.attachments.map((a, i) => (
                          <div key={i} className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 px-3 py-1.5 rounded text-sm">
                            <a href={normalizeFileUrl(a.url)} target="_blank" rel="noopener noreferrer" className="text-orange-500 underline">{a.filename}</a>
                            <button onClick={() => handleDeleteAttachment(detail.report_id, a.attachment_id)} className="text-red-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {detail.follow_up_comment && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                      <p className="text-xs font-medium text-blue-600 mb-1">{t('admin.bug.userFollowup')}</p>
                      <p className="text-sm">{detail.follow_up_comment.comment}</p>
                      <p className="text-xs text-muted-foreground mt-1">{new Date(detail.follow_up_comment.created_at).toLocaleString('de-DE')}</p>
                    </div>
                  )}
                  {detail.duplicate_of && (
                    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded">
                      <p className="text-sm">Duplicate of <strong className="font-mono">{detail.duplicate_of}</strong></p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="notes" className="space-y-4 mt-3">
                  {/* Internal notes */}
                  <div>
                    <p className="text-sm font-medium mb-2">{t('admin.bug.internalNotes')}</p>
                    {detail.internal_notes?.length > 0 ? detail.internal_notes.map((n, i) => (
                      <div key={i} className="text-sm bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded mb-2">
                        <p>{n.note}</p>
                        <p className="text-xs text-muted-foreground mt-1">{n.added_by_name} — {new Date(n.added_at).toLocaleString('de-DE')}</p>
                      </div>
                    )) : <p className="text-sm text-muted-foreground">{t('admin.bug.noInternalNotes')}</p>}
                  </div>
                  <Separator />
                  {/* Resolution notes */}
                  <div>
                    <p className="text-sm font-medium mb-2">{t('admin.bug.resolutionNotes')}</p>
                    {detail.resolution_notes?.length > 0 ? detail.resolution_notes.map((n, i) => (
                      <div key={i} className="text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded mb-2">
                        <p>{n.note}</p>
                        <p className="text-xs text-muted-foreground mt-1">{new Date(n.added_at).toLocaleString('de-DE')}</p>
                      </div>
                    )) : <p className="text-sm text-muted-foreground">{t('admin.bug.noResolutionNotes')}</p>}
                  </div>
                  <Separator />
                  {/* Status history */}
                  <div>
                    <p className="text-sm font-medium mb-2">{t('admin.bug.statusHistory')}</p>
                    <div className="space-y-2">
                      {(detail.status_history || []).map((sh, i) => (
                        <div key={i} className="flex items-start gap-3 text-xs">
                          <span className="font-mono text-muted-foreground w-32 shrink-0">{new Date(sh.changed_at).toLocaleString('de-DE')}</span>
                          <Badge className={(STATUS_MAP[sh.status] || STATUS_MAP.NEW).cls} variant="secondary">{sh.status.replace('_', ' ')}</Badge>
                          {sh.note && <span className="text-muted-foreground">— {sh.note}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="audit" className="mt-3">
                  <div className="space-y-2">
                    {(detail.audit_log || []).map((a, i) => (
                      <div key={i} className="flex items-start gap-3 text-xs border-b pb-2">
                        <span className="font-mono text-muted-foreground w-32 shrink-0">{new Date(a.timestamp).toLocaleString('de-DE')}</span>
                        <span className="font-medium">{a.action}</span>
                        <span className="text-muted-foreground">by {a.actor_name}</span>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Status Change Dialog ── */}
      <Dialog open={!!statusDialog} onOpenChange={() => setStatusDialog(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle>{t('admin.bug.changeStatus')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger data-testid="status-change-select"><SelectValue placeholder={t("admin.bug.selectStatus")} /></SelectTrigger>
              <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea placeholder={t("admin.bug.mandatoryNote")} value={statusNote} onChange={e => setStatusNote(e.target.value)} rows={3} data-testid="status-change-note" />
          </div>
          <DialogFooter><Button onClick={handleStatusChange} disabled={!newStatus || !statusNote.trim()} className="bg-orange-500 hover:bg-orange-600" data-testid="status-change-confirm">{t('admin.bug.confirm')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Assign Dialog ── */}
      <Dialog open={!!assignDialog} onOpenChange={() => setAssignDialog(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle>{t('admin.bug.assignToAdmin')}</DialogTitle></DialogHeader>
          <Select value={assignTo} onValueChange={setAssignTo}>
            <SelectTrigger data-testid="assign-select"><SelectValue placeholder={t("admin.bug.selectAdmin")} /></SelectTrigger>
            <SelectContent>{admins.map(a => <SelectItem key={a.user_id} value={a.user_id}>{a.full_name} ({a.email})</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter><Button onClick={handleAssign} disabled={!assignTo} className="bg-orange-500 hover:bg-orange-600" data-testid="assign-confirm">Assign</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Duplicate Dialog ── */}
      <Dialog open={!!dupDialog} onOpenChange={() => setDupDialog(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle>{t('admin.bug.markAsDuplicate')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder={t("admin.bug.originalReportId")} value={dupRef} onChange={e => setDupRef(e.target.value)} data-testid="duplicate-ref-input" />
            <Textarea placeholder="Note (optional)" value={dupNote} onChange={e => setDupNote(e.target.value)} rows={2} />
          </div>
          <DialogFooter><Button onClick={handleDuplicate} disabled={!dupRef.trim()} className="bg-orange-500 hover:bg-orange-600" data-testid="duplicate-confirm">{t('admin.bug.confirm')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add Note Dialog ── */}
      <Dialog open={!!noteDialog} onOpenChange={() => setNoteDialog(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle>{t('admin.bug.addNote')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={noteType} onValueChange={setNoteType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="internal">{t('admin.bug.internal')}</SelectItem>
                <SelectItem value="resolution">{t('admin.bug.resolutionVisible')}</SelectItem>
              </SelectContent>
            </Select>
            <Textarea placeholder={t("admin.bug.noteTextPh")} value={noteText} onChange={e => setNoteText(e.target.value)} rows={3} data-testid="add-note-textarea" />
          </div>
          <DialogFooter><Button onClick={handleAddNote} disabled={!noteText.trim()} className="bg-orange-500 hover:bg-orange-600" data-testid="add-note-confirm">{t('admin.bug.addNote')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Bulk Action Dialog ── */}
      <Dialog open={bulkDialog} onOpenChange={setBulkDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle>Bulk Action ({selected.length} reports)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={bulkAction} onValueChange={setBulkAction}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="change_status">{t('admin.bug.changeStatus')}</SelectItem>
                <SelectItem value="reassign">{t('admin.bug.reassign')}</SelectItem>
              </SelectContent>
            </Select>
            {bulkAction === 'change_status' && (
              <>
                <Select value={bulkStatus} onValueChange={setBulkStatus}>
                  <SelectTrigger><SelectValue placeholder={t("admin.bug.newStatus")} /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}</SelectContent>
                </Select>
                <Textarea placeholder={t("admin.bug.notePlaceholder")} value={bulkNote} onChange={e => setBulkNote(e.target.value)} rows={2} />
              </>
            )}
            {bulkAction === 'reassign' && (
              <Select value={bulkAssignee} onValueChange={setBulkAssignee}>
                <SelectTrigger><SelectValue placeholder={t("admin.bug.selectAdmin")} /></SelectTrigger>
                <SelectContent>{admins.map(a => <SelectItem key={a.user_id} value={a.user_id}>{a.full_name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter><Button onClick={handleBulkAction} className="bg-orange-500 hover:bg-orange-600">Apply</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
